// src/components/GenerateOldPhotosRestorationModal.jsx
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Dialog } from "@headlessui/react";
import {
  X,
  Image as ImageIcon,
  Wand2,
  Plus,
  Trash2,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

const steps = [
  { number: 1, label: "Select Image" },
  { number: 2, label: "Quality" },
];

function StepperHeader({ step }) {
  return (
    <div className="flex items-center gap-2 px-8 pt-7 pb-2">
      {steps.map((s, idx) => (
        <React.Fragment key={s.number}>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === s.number
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            {s.number}
          </span>
          <span
            className={`text-base sm:text-lg font-semibold ${
              step === s.number ? "text-white" : "text-gray-400"
            } ${idx !== steps.length - 1 ? "mr-8" : ""}`}
          >
            {s.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

StepperHeader.propTypes = {
  step: PropTypes.number.isRequired,
};

function StepFooter({ step, onBack, onNext, onGenerate, canNext, canGenerate }) {
  return (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#2B2B4A] flex justify-between items-center px-8 py-4 z-20">
      <button
        className="px-7 py-2 rounded-xl font-bold bg-[#222246] hover:bg-[#2B2B4A] text-white disabled:opacity-40"
        disabled={step === 1}
        onClick={onBack}
        type="button"
      >
        Back
      </button>

      {step === 1 ? (
        <button
          className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40"
          onClick={onNext}
          disabled={!canNext}
          type="button"
        >
          Next
        </button>
      ) : (
        <button
          className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40"
          onClick={onGenerate}
          disabled={!canGenerate}
          type="button"
        >
          Generate
        </button>
      )}
    </div>
  );
}

StepFooter.propTypes = {
  step: PropTypes.number.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  canNext: PropTypes.bool.isRequired,
  canGenerate: PropTypes.bool.isRequired,
};

export default function GenerateOldPhotosRestorationModal({
  productImages = [],
  onClose,
  onGenerate,
}) {
  const { getToken } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [quality, setQuality] = useState("2k"); // '2k' | '4k'

  // ✅ Step 1 manual upload (EXACT same flow as Ad Marketing Video step 1)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const [error, setError] = useState(null);
  const step1FileInputRef = useRef(null);

  const onBack = () => setStep((s) => Math.max(1, s - 1));
  const onNext = () => {
    if (!selectedImage) return;
    setStep(2);
  };

  const handleGenerate = () => {
    if (!selectedImage || !quality) return;
    onGenerate(selectedImage, quality);
    onClose();
  };

  const handleStep1Upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStep1UploadLoading(true);
    setError(null);

    try {
      if (file.size > 20 * 1024 * 1024) {
        setError("Image is too large. Please upload a file under 20MB.");
        return;
      }

      const token = await getToken();
      const formData = new FormData();
      formData.append("file", file);

      // Reuse existing upload endpoint (same as End card upload)
      const { data } = await axios.post(
        `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const url = data?.url;
      if (url) {
        const newAsset = { id: `asset-upload-${Date.now()}`, url };
        setStep1UploadedAsset(newAsset);
        setSelectedImage(url); // single-select -> auto-select uploaded
      }
    } catch {
      setError("Failed to upload image.");
    } finally {
      setStep1UploadLoading(false);
      event.target.value = "";
    }
  };

  const isUploadedSelected =
    !!step1UploadedAsset && selectedImage === step1UploadedAsset.url;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="relative bg-[#191930] text-white rounded-2xl shadow-2xl w-full max-w-[880px] min-h-[410px] h-[82vh] flex flex-col overflow-hidden">
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
          type="button"
        >
          <X size={22} />
        </button>

        <StepperHeader step={step} />

        {/* Scrollable content (same behavior as Ad Marketing Video modal) */}
        <div
          id="old-photos-restoration-modal-scroll"
          className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto"
        >
          {/* Step 1: Image */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select an old photo to restore.
                {selectedImage && (
                  <span className="text-gray-400 text-sm">Selected 1 / 1</span>
                )}
              </p>

              {error && (
                <div className="mb-3 text-red-500 font-semibold">{error}</div>
              )}

              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              {/* ✅ Always show the upload tile (even if productImages is empty) */}
              <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                <div className="grid grid-cols-5 gap-4">
                  {/* ✅ Upload tile (same size as assets; becomes selectable once uploaded) */}
                  <div
                    className={`relative rounded-lg overflow-hidden border-2 group transition ${
                      step1UploadedAsset
                        ? isUploadedSelected
                          ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                          : "border-transparent hover:border-pink-400 cursor-pointer"
                        : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (step1UploadLoading) return;

                      if (step1UploadedAsset) {
                        setSelectedImage(step1UploadedAsset.url);
                        return;
                      }

                      step1FileInputRef.current?.click();
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (step1UploadLoading) return;

                        if (step1UploadedAsset) {
                          setSelectedImage(step1UploadedAsset.url);
                          return;
                        }

                        step1FileInputRef.current?.click();
                      }
                    }}
                    aria-label={step1UploadedAsset ? "Uploaded image" : "Upload image"}
                  >
                    <div className="aspect-[4/5] bg-black relative">
                      {step1UploadedAsset ? (
                        <img
                          className="object-cover w-full h-full"
                          src={step1UploadedAsset.url}
                          alt="Uploaded"
                          loading="lazy"
                          draggable={false}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                          <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                            <Plus size={22} className="text-white" />
                          </div>
                          <div className="text-white font-semibold text-sm">
                            Upload image
                          </div>
                          <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                            JPG, PNG, WEBP
                          </div>
                        </div>
                      )}

                      {step1UploadLoading && (
                        <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                          <Loader2 className="animate-spin text-white" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                    </div>

                    {/* Actions (only when uploaded) */}
                    {step1UploadedAsset && (
                      <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                        <button
                          type="button"
                          className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (step1UploadLoading) return;
                            step1FileInputRef.current?.click();
                          }}
                          aria-label="Replace uploaded image"
                          disabled={step1UploadLoading}
                        >
                          Replace
                        </button>

                        <button
                          type="button"
                          className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            const uploadedUrl = step1UploadedAsset.url;
                            setStep1UploadedAsset(null);
                            if (selectedImage === uploadedUrl) {
                              setSelectedImage(null);
                            }
                          }}
                          aria-label="Remove uploaded image"
                          disabled={step1UploadLoading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Existing analyzed images */}
                  {productImages.map((img, i) => {
                    const isSelected = selectedImage === img;
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition ${
                          isSelected
                            ? "border-pink-500 ring-2 ring-pink-600"
                            : "border-transparent hover:border-pink-400"
                        }`}
                        onClick={() => setSelectedImage(img)}
                        title="Select image"
                        aria-label="Select image"
                      >
                        <div className="aspect-[4/5] bg-black">
                          <img
                            src={img}
                            alt="Old"
                            className="object-cover w-full h-full"
                            loading="lazy"
                            draggable={false}
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {productImages.length === 0 && (
                <div className="text-white/70 text-center py-6 select-none">
                  No product images found. You can still upload an image above.
                </div>
              )}
            </>
          )}

          {/* Step 2: Quality */}
          {step === 2 && (
            <div className="mt-2">
              <p className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Wand2 size={18} /> Choose quality
              </p>

              <div className="grid grid-cols-2 gap-4">
                {["2k", "4k"].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQuality(q)}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition min-h-[92px] ${
                      quality === q
                        ? "border-pink-500 bg-[#2A2A40]"
                        : "border-transparent bg-[#1F1F2F] hover:border-gray-600"
                    }`}
                  >
                    <ImageIcon className="mb-2" size={28} />
                    <span className="font-semibold text-sm">
                      {q.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-400">
                      {q === "2k" ? "Faster" : "Higher quality"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <StepFooter
          step={step}
          onBack={onBack}
          onNext={onNext}
          onGenerate={handleGenerate}
          canNext={!!selectedImage}
          canGenerate={!!selectedImage && !!quality}
        />
      </div>
    </Dialog>
  );
}

GenerateOldPhotosRestorationModal.propTypes = {
  productImages: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};
